import { apiFetch } from './api';
import type { ApiResponse } from './RegrasIAService';

/**
 * Roteamento de conversa por intenção — `docs/requisicoes-http/RoteamentoIa.md`
 * na API. Cada regra é uma intenção em texto livre + um destino (pessoa OU
 * equipe). Quando a IA transfere a conversa, ela diz qual intenção casou e o
 * back-end entrega para o destino daquela regra.
 *
 * Não existe liga/desliga do recurso: o agente roteia por intenção quando tem
 * ao menos uma regra ativa. Sem regras, segue o rodízio normal da empresa.
 */

/** `TipoDestinoRoteamentoEnum` da API. */
export const TIPO_DESTINO = {
    Usuario: 1,
    Equipe: 2,
} as const;

export type TipoDestino = typeof TIPO_DESTINO[keyof typeof TIPO_DESTINO];

/** Limite do campo `intent` no banco (varchar 500). */
export const MAX_INTENCAO = 500;

export interface RegraRoteamento {
    id: number;
    agentConfigId: number;
    intencao: string;
    tipoDestino: TipoDestino;
    /** `userId` quando a regra aponta para pessoa; `equipeId` quando aponta para equipe. */
    destinoId: number;
    destinoNome: string;
    /**
     * `false` quando a pessoa foi excluída ou a equipe ficou sem membro ativo.
     * A regra continua salva, mas em runtime cai no rodízio geral.
     *
     * Atenção: o `Atualizar` da API revalida o destino em TODA chamada, até
     * para só desativar. Regra com destino inválido não desativa — tem que
     * trocar o destino ou excluir.
     */
    destinoValido: boolean;
    ativa: boolean;
}

export interface CriarRegraRoteamentoDTO {
    agentConfigId: number;
    intencao: string;
    tipoDestino: TipoDestino;
    destinoId: number;
}

export interface AtualizarRegraRoteamentoDTO {
    id: number;
    intencao: string;
    tipoDestino: TipoDestino;
    destinoId: number;
    ativa: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || '';

export const RoteamentoIaService = {
    porAgente: async (agentConfigId: number): Promise<ApiResponse<RegraRoteamento[]>> => {
        const response = await apiFetch(`${API_URL}/api/RoteamentoIa/PorAgente/${agentConfigId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        const data = await response.json();
        if (!response.ok && !data.mensagem) {
            throw new Error('Erro ao buscar regras de roteamento');
        }
        return data;
    },

    criar: async (regra: CriarRegraRoteamentoDTO): Promise<ApiResponse<RegraRoteamento>> => {
        const response = await apiFetch(`${API_URL}/api/RoteamentoIa/Criar`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(regra),
        });
        const data = await response.json();
        if (!response.ok && !data.mensagem) {
            throw new Error('Erro ao criar regra de roteamento');
        }
        return data;
    },

    atualizar: async (regra: AtualizarRegraRoteamentoDTO): Promise<ApiResponse<null>> => {
        const response = await apiFetch(`${API_URL}/api/RoteamentoIa/Atualizar`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(regra),
        });
        const data = await response.json();
        if (!response.ok && !data.mensagem) {
            throw new Error('Erro ao atualizar regra de roteamento');
        }
        return data;
    },

    remover: async (id: number): Promise<ApiResponse<null>> => {
        const response = await apiFetch(`${API_URL}/api/RoteamentoIa/Remover/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        const data = await response.json();
        if (!response.ok && !data.mensagem) {
            throw new Error('Erro ao remover regra de roteamento');
        }
        return data;
    },
};
